import React from 'react'
import { isNil, isEmpty } from 'lodash-es'
import { NegotiationDetailWithMeta, NegotiationDetail, NegotiationPackageDetail, CollectionType, NegotiationType } from 'api'
import { ActionsButtonProps } from './ActionsRenderer'
import { DefaultCellContentWrapper } from './DefaultCellRenderer'
import { iconSet } from 'view/components/icons'
import { MultiButton } from 'view/components/buttons/MultiButton'
import styled from '@emotion/styled'
import { Tooltip } from 'view/components/tooltip'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { ButtonStrip } from 'view/components/buttons'
import { TypedCellRendererParams, TypedRowNode } from '../gridUtils'
import { NEGOTIATIONS_PATH } from './negotiationDataUtil'
import { canCancel } from 'view/routes/requests/shared/utils'

export interface NegotiationActionRendererProps extends TypedCellRendererParams<NegotiationDetail> {
  canCancelPackage: boolean
  data: NegotiationPackageDetail
  node: TypedRowNode<NegotiationPackageDetail>
  onCancelPackage: () => void
  onCancelBulk: (negotiationIds: string[], canCancelAndStage: boolean) => void
  selectedNegotiationDetail?: NegotiationDetailWithMeta
  selectedNegotiationDetailSummary?: NegotiationDetail
  getNegotiationCountWithRfqId: (rfqId: string) => number
}

const cancelWidth = '15px'

const CancelButtonWrapper = styled.div`
  min-width: ${cancelWidth};
`

const CounterpartyActionsWrapper = styled(DefaultCellContentWrapper)`
  justify-content: flex-end;

  > div:first-of-type:not(${CancelButtonWrapper}) {
    width: calc(100% - ${cancelWidth});
  }

  ${ButtonStrip} {
    justify-content: flex-end;

    button:first-of-type {
      margin-right: auto;
    }
  }
`

const CancelButton = styled(MultiButton)`
  color: ${constants.CORE_PRIMARY_5};
  display: inline-block;
  height: 26px;
  width: 100%;

  svg {
    width: 10px;
    height: 10px;
  }
`

export function createCounterpartyActionsRenderer(ActionsButton: React.ComponentType<ActionsButtonProps<NegotiationDetailWithMeta>>) {
  return class NegotiationActionsRenderer extends React.Component<NegotiationActionRendererProps> {
    render() {
      const {
        context,
        selectedNegotiationDetail,
        selectedNegotiationDetailSummary,
        node: { group, data },
        onCancelPackage,
        canCancelPackage
      } = this.props

      if (group) {
        return (
          <CounterpartyActionsWrapper rightAlign={true}>
            {isNil(selectedNegotiationDetailSummary) || (
              <ActionsButton
                context={context}
                isCounterpartyNegotiation={true}
                isGroupRow={true}
                items={[selectedNegotiationDetailSummary]}
              />
            )}
            <CancelButtonWrapper>
              {canCancelPackage && (
                <Tooltip tooltip={Locale.translate(Translation.SC_CANCEL_RFQS)}>
                  <CancelButton onClick={onCancelPackage} dataTestId="CounterpartyAction-Cancel-Collection">
                    {iconSet.crossIcon}
                  </CancelButton>
                </Tooltip>
              )}
            </CancelButtonWrapper>
          </CounterpartyActionsWrapper>
        )
      }

      if (isNil(data) || isNil(data[NEGOTIATIONS_PATH])) {
        return null
      }

      const negotiations = Object.values(data[NEGOTIATIONS_PATH])
      const canCancelNegotiations =
        !isEmpty(negotiations) &&
        negotiations.every(negotiationDetail => canCancel(negotiationDetail)) &&
        negotiations[0].collection?.type !== CollectionType.ALL_OR_NOTHING

      if (!group) {
        return (
          <CounterpartyActionsWrapper>
            {isNil(selectedNegotiationDetail) || (
              <ActionsButton items={[selectedNegotiationDetail]} isGroupRow={false} context={context} isCounterpartyNegotiation={true} />
            )}
            <CancelButtonWrapper>
              {canCancelNegotiations && (
                <Tooltip tooltip={Locale.translate(Translation.SC_CANCEL_RFQS)}>
                  <CancelButton onClick={this.handleCancelNegotiations} dataTestId="CounterpartyAction-Cancel-Individual">
                    {iconSet.crossIcon}
                  </CancelButton>
                </Tooltip>
              )}
            </CancelButtonWrapper>
          </CounterpartyActionsWrapper>
        )
      }

      return null
    }

    private handleCancelNegotiations = () => {
      const {
        props: { data, getNegotiationCountWithRfqId, onCancelBulk }
      } = this

      const canCancelAndStage = Object.values(data[NEGOTIATIONS_PATH]).every(negotiationDetail => {
        if (negotiationDetail.negotiation.negotiationType === NegotiationType.INDIVIDUAL) {
          const { parentOrderId } = negotiationDetail.negotiation
          const totalNumberOfNegotiationsWithParentOrderId = getNegotiationCountWithRfqId(parentOrderId)

          return (
            Object.values(data[NEGOTIATIONS_PATH]).filter(({ negotiation }) => negotiation.parentOrderId === parentOrderId).length ===
            totalNumberOfNegotiationsWithParentOrderId
          )
        } else {
          return false
        }
      })

      const negotiationIds = Object.values(data[NEGOTIATIONS_PATH]).map(negotiationDetail => negotiationDetail.negotiation.negotiationId)
      onCancelBulk(negotiationIds, canCancelAndStage)
    }

    refresh = () => {
      this.forceUpdate()
      return true
    }
  }
}
